import React, { useEffect, useState } from 'react';
import { LockIcon, AlertCircleIcon, UserIcon } from 'lucide-react';
interface LoginProps {
  onLogin: (username: string, password: string) => void;
  loginError: string | null;
  remainingAttempts: number | null;
  lockoutTime: number | null;
}
export function Login({
  onLogin,
  loginError,
  remainingAttempts,
  lockoutTime
}: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [lockoutTimeRemaining, setLockoutTimeRemaining] = useState<number | null>(lockoutTime);
  // Handle countdown timer if account is locked
  useEffect(() => {
    if (!lockoutTime) {
      setLockoutTimeRemaining(null);
      return;
    }
    setLockoutTimeRemaining(lockoutTime);
    const interval = setInterval(() => {
      setLockoutTimeRemaining(prev => {
        if (!prev || prev <= 0) {
          clearInterval(interval);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutTime]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && password.trim()) {
      onLogin(username, password);
    }
  };
  const formatTimeRemaining = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  return <div className="min-h-screen flex items-center justify-center bg-black" data-id="element-1601">
      <div className="max-w-md w-full p-6 bg-[#0A0A0A] rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.5)] border border-[#222222]" data-id="element-1602">
        <div className="text-center mb-6" data-id="element-1603">
          <div className="flex justify-center mb-1" data-id="element-1604">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vseaUET8yPx3mvKjBCRrif/Ghisha_Logo_0-_black.001-Photoroom.png" alt="Ghisha Logo" className="h-32 object-contain" data-id="element-1605" />
          </div>
          <p className="text-gray-400" data-id="element-1606">AI Lifecycle Management Platform</p>
        </div>
        {lockoutTimeRemaining ? <div className="bg-[#1A0A0A] border border-red-900 rounded-md p-4 mb-6" data-id="element-1607">
            <div className="flex" data-id="element-1608">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-1609" />
              <div data-id="element-1610">
                <h3 className="text-sm font-medium text-red-400" data-id="element-1611">
                  Account temporarily locked
                </h3>
                <p className="mt-1 text-sm text-red-300" data-id="element-1612">
                  Too many failed login attempts. Please try again in{' '}
                  {formatTimeRemaining(lockoutTimeRemaining)}.
                </p>
              </div>
            </div>
          </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1613">
            <div data-id="element-1614">
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1615">
                Username
              </label>
              <div className="relative" data-id="element-1616">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1617">
                  <UserIcon className="h-5 w-5 text-gray-500" data-id="element-1618" />
                </div>
                <input id="username" name="username" type="text" value={username} onChange={e => setUsername(e.target.value)} required className="pl-10 block w-full bg-[#121212] border border-[#333333] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-white focus:border-[#444444] text-white" placeholder="Enter your username" data-id="element-1619" />
              </div>
            </div>
            <div data-id="element-1620">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1621">
                Password
              </label>
              <div className="relative" data-id="element-1622">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1623">
                  <LockIcon className="h-5 w-5 text-gray-500" data-id="element-1624" />
                </div>
                <input id="password" name="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="pl-10 block w-full bg-[#121212] border border-[#333333] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-white focus:border-[#444444] text-white" placeholder="Enter your password" data-id="element-1625" />
              </div>
            </div>
            {loginError && <div className="bg-[#1A0A0A] border border-red-900 rounded-md p-3" data-id="element-1626">
                <div className="flex" data-id="element-1627">
                  <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-1628" />
                  <div data-id="element-1629">
                    <p className="text-sm text-red-400" data-id="element-1630">{loginError}</p>
                    {remainingAttempts !== null && <p className="text-xs text-red-300 mt-1" data-id="element-1631">
                        {remainingAttempts} login attempts remaining before
                        temporary lockout.
                      </p>}
                  </div>
                </div>
              </div>}
            <div data-id="element-1632">
              <button type="submit" disabled={!!lockoutTimeRemaining} className="w-full flex justify-center py-2 px-4 border border-[#333333] rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#333333] to-[#111111] hover:from-[#444444] hover:to-[#222222] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300" data-id="element-1633">
                Sign in
              </button>
            </div>
          </form>}
      </div>
    </div>;
}